import { getMonth, getYear, setMonth, setYear } from "date-fns";
import type { Locale, Month } from "date-fns";
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  useTheme,
  Typography,
  Grid,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import { AVAILABLE_MAX_DATE, AVAILABLE_MIN_DATE } from "../Constants";

type MonthHeaderProps = {
  minDate: Date;
  maxDate: Date;
  marker: symbol;
  currentDate: Date;
  otherDate: Date;
  setDate: (date: Date) => void;
  nextDisabled: boolean;
  prevDisabled: boolean;
  onClickNext: () => void;
  onClickPrevious: () => void;
  locale?: Locale;
};

const generateYears = ({ start, end }: { start: number; end: number }) => {
  const count = end - start + 1;

  return Array(count)
    .fill(0)
    .map((_y, i) => start + i);
};

export const MonthHeader = ({
  minDate,
  maxDate,
  currentDate,
  setDate,
  nextDisabled,
  prevDisabled,
  onClickNext,
  onClickPrevious,
  locale,
}: MonthHeaderProps) => {
  const theme = useTheme();

  const availableYearRange = {
    start: ((minDate as Date) || AVAILABLE_MIN_DATE).getFullYear(),
    end: ((maxDate as Date) || AVAILABLE_MAX_DATE).getFullYear(),
  };

  const MONTHS = Array.from({ length: 12 }, (_, index) =>
    typeof locale !== "undefined"
      ? locale.localize?.month(index as Month, {
          width: "abbreviated",
          context: "standalone",
        })
      : [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ][index]
  );

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setDate(setMonth(currentDate, parseInt(event.target.value as string, 10)));
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setDate(setYear(currentDate, parseInt(event.target.value as string, 10)));
  };

  const currentMonth = getMonth(currentDate);
  const currentYear = getYear(currentDate);
  // const otherMonth = getMonth(otherDate);
  // const otherYear = getYear(otherDate);

  const minYear = getYear(minDate);
  const maxYear = getYear(maxDate);
  const minMonthID = getMonth(minDate);
  const maxMonthID = getMonth(maxDate);

  const isDisabled = (month: number) => {
    // const isSameMonthSameYear =
    //   month === otherMonth && currentYear === otherYear;

    // if (marker !== MARKERS.SINGLE_MONTH) {
    // if (isSameMonthSameYear) {
    //   return true;
    // } else if (currentYear === otherYear) {
    //   if (isFirstMonth && month > otherMonth) {
    //     return true;
    //   } else if (!isFirstMonth && month < otherMonth) {
    //     return true;
    //   }
    // }
    // }

    // validations for out of given range
    if (currentYear === minYear || currentYear === maxYear) {
      if (currentYear === minYear && month < minMonthID) {
        return true;
      }
      if (currentYear === maxYear && month > maxMonthID) {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <Grid xs="auto" ml="10px" container>
        <IconButton
          disableRipple
          size="small"
          color="secondary"
          className="Mui-upon-secondary-bg"
          disabled={prevDisabled}
          onClick={onClickPrevious}
          sx={{
            borderRadius: "8px",
            color: theme.palette.primary.main,
            ".MuiSvgIcon-root": {
              color: theme.palette.primary.main,
            },
            "&:hover": {
              backgroundColor: theme.palette.background.default,
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
            },

            "&.Mui-upon-secondary-bg": {
              "&:hover": {
                backgroundColor: theme.palette.background.paper,
              },
            },
          }}
        >
          <KeyboardArrowLeft
            fontSize="small"
            sx={{
              fill: prevDisabled ? `${theme.palette.primary.light}` : "secondary",
            }}
          />
        </IconButton>
      </Grid>

      <Grid xs={"auto"} container>
        <FormControl>
          <Select
            SelectDisplayProps={{
              style: {
                minHeight: "unset",
              },
            }}
            variant="outlined"
            size="small"
            defaultValue={currentYear}
            IconComponent={(props) => (
              <KeyboardArrowDown
                fontSize="small"
                sx={{
                  fill: theme.palette.primary.main,
                }}
                {...props}
              />
            )}
            slotProps={{
              root: {
                sx: {
                  width: {
                    sm: "100px",
                    md: "120px",
                  },
                  height: "30px",
                },
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  width: "auto",
                  maxHeight: "224px",
                  boxShadow:
                    "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
                },
              },
            }}
            value={currentMonth}
            onChange={handleMonthChange}
          >
            {MONTHS.map((month, idx) => {
              return (
                <MenuItem key={month} value={idx} disabled={isDisabled(idx)}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    {month}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid xs="auto" container>
        <FormControl>
          <Select
            variant="outlined"
            size="small"
            defaultValue={currentYear}
            SelectDisplayProps={{
              style: {
                minHeight: "unset",
              },
            }}
            IconComponent={(props) => (
              <KeyboardArrowDown
                fontSize="small"
                sx={{
                  fill: theme.palette.primary.main,
                }}
                {...props}
              />
            )}
            slotProps={{
              root: {
                sx: {
                  height: "30px",
                },
              },
            }}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  width: "auto",
                  maxHeight: "224px",
                  boxShadow:
                    "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
                },
              },
            }}
            value={getYear(currentDate)}
            onChange={handleYearChange}
          >
            {generateYears(availableYearRange).map((year) => {
              // let disabled = false;
              //
              // if (marker !== MARKERS.SINGLE_MONTH) {
              //   if (isFirstYear && year > otherYear) {
              //     disabled = true;
              //   } else if (!isFirstYear && year < otherYear) {
              //     disabled = true;
              //   }
              // }

              return (
                <MenuItem key={year} value={year}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                    }}
                  >
                    {year}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid mr="10px" xs="auto" container>
        <IconButton
          disableRipple
          size="small"
          color="secondary"
          className="Mui-upon-secondary-bg"
          disabled={nextDisabled}
          onClick={onClickNext}
          sx={{
            borderRadius: "8px",
            color: theme.palette.primary.main,
            ".MuiSvgIcon-root": {
              color: theme.palette.primary.main,
            },
            "&:hover": {
              backgroundColor: theme.palette.primary.light,
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
            },

            "&.Mui-upon-secondary-bg": {
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
            },
          }}
        >
          <KeyboardArrowRight
            fontSize="small"
            sx={{
              fill: nextDisabled ? `${theme.palette.primary.light}` : "secondary",
            }}
          />
        </IconButton>
      </Grid>
    </>
  );
};
